import { useState, useEffect, useCallback } from 'react';
import { Activity, Shield, AlertTriangle, CheckCircle, Zap, Target, Database, TrendingUp, FileSearch, ListTodo, RefreshCw, ChevronRight, Brain, BarChart3, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_BASE = '';

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', glow: 'shadow-[0_0_15px_rgba(239,68,68,0.15)]' },
  high: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', glow: '' },
  medium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', glow: '' },
  low: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', glow: '' },
  info: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', glow: '' },
};

function ScoreRing({ score, size = 120, label }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black text-white tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{score}</span>
        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color = 'white', sub }) {
  return (
    <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800/50 rounded-xl p-4 hover:border-purple-500/20 transition-all group">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-4 h-4 text-${color}`} />
        {sub && <span className="text-[9px] text-slate-600">{sub}</span>}
      </div>
      <p className="text-2xl font-bold text-white tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function InsightCard({ insight }) {
  const style = SEVERITY_COLORS[insight.severity] || SEVERITY_COLORS.info;
  return (
    <div className={`${style.bg} border ${style.border} ${style.glow} rounded-xl p-4 transition-all hover:scale-[1.01]`}>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${style.text} ${style.bg} border ${style.border} px-2 py-0.5 rounded-full`}>
          {insight.severity}
        </span>
        <span className="text-[10px] text-slate-500">{insight.confidence}% confidence</span>
      </div>
      <h4 className="text-sm font-bold text-white mb-1">{insight.title}</h4>
      <p className="text-xs text-slate-400 leading-relaxed mb-3">{insight.explanation?.slice(0, 200)}{insight.explanation?.length > 200 ? '...' : ''}</p>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-500">{insight.affected_pages} pages affected</span>
        <span className={style.text}>{insight.impact}</span>
      </div>
    </div>
  );
}

function TaskRow({ task }) {
  const prioColors = { P0: 'text-red-400 bg-red-500/10 border-red-500/30', P1: 'text-orange-400 bg-orange-500/10 border-orange-500/30', P2: 'text-amber-400 bg-amber-500/10 border-amber-500/30', P3: 'text-blue-400 bg-blue-500/10 border-blue-500/30' };
  const pc = prioColors[task.priority] || prioColors.P3;
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors">
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${pc}`}>{task.priority}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-200 truncate">{task.title?.replace(`[${task.priority}] `, '')}</p>
        <p className="text-[10px] text-slate-500">{task.affected_pages} pages | {task.estimated_impact}</p>
      </div>
      <span className={`text-[10px] px-2 py-0.5 rounded ${task.status === 'open' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>{task.status}</span>
    </div>
  );
}

export default function SEOOSOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/seoos/overview`, { headers })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAudit = async () => {
    setAuditing(true);
    await fetch(`${API_BASE}/api/seoos/run-audit`, { method: 'POST', headers });
    load();
    setAuditing(false);
  };

  if (loading || !data) return (
    <div className="flex items-center justify-center h-96">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const { kpis, insights, tasks, last_audit, audit, index } = data;

  return (
    <div data-testid="seoos-overview" className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-3" style={{ fontFamily: 'var(--font-heading)' }}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-violet-700 flex items-center justify-center shadow-[0_0_20px_rgba(147,51,234,0.3)]">
              <Brain className="w-5 h-5 text-white" />
            </div>
            SEOOS
            <span className="text-xs font-normal text-purple-400/60 ml-1">v1.0 Genesis</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">SEO Operating System - AI-Powered SEO Intelligence</p>
        </div>
        <div className="flex items-center gap-3">
          {last_audit && <span className="text-[10px] text-slate-600">Last audit: {new Date(last_audit).toLocaleString('tr-TR')}</span>}
          <Button onClick={runAudit} disabled={auditing} className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold px-4">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${auditing ? 'animate-spin' : ''}`} />
            {auditing ? 'Analiz ediliyor...' : 'Tam Audit Calistir'}
          </Button>
        </div>
      </div>

      {/* SCORE RINGS + KPIs */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 bg-gradient-to-br from-slate-900 via-purple-950/20 to-slate-900 border border-purple-500/20 rounded-2xl p-6 flex items-center justify-around shadow-[0_0_30px_rgba(147,51,234,0.08)]">
          <ScoreRing score={kpis.seo_health} label="SEO Health" />
          <ScoreRing score={kpis.index_score} size={90} label="Index" />
        </div>
        <div className="col-span-8 grid grid-cols-4 gap-3">
          <KPICard icon={Globe} label="İndekslenebilir" value={kpis.indexable_pages} color="purple-400" />
          <KPICard icon={Database} label="Toplam Maç" value={kpis.total_matches} color="blue-400" sub={`Futbol: ${kpis.football_matches || kpis.total_matches}`} />
          <KPICard icon={BarChart3} label="Oranlı Maç" value={kpis.prematch_with_odds} color="emerald-400" />
          <KPICard icon={FileSearch} label="SEO İçerik" value={kpis.seo_content_pages} color="amber-400" />
          <KPICard icon={AlertTriangle} label="Kritik Sorun" value={kpis.critical_issues} color="red-400" />
          <KPICard icon={Zap} label="Yüksek Öncelik" value={kpis.high_issues} color="orange-400" />
          <KPICard icon={ListTodo} label="Açık Görev" value={kpis.open_tasks} color="violet-400" />
          <KPICard icon={Shield} label="Bahis Siteleri" value={kpis.bookmakers} color="cyan-400" />
        </div>
      </div>

      {/* AI INSIGHTS + TASKS */}
      <div className="grid grid-cols-12 gap-4">
        {/* Insights */}
        <div className="col-span-7">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-purple-400" />
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">AI Insights</h2>
            <span className="text-[10px] text-slate-600">{insights?.length || 0} insights</span>
          </div>
          <div className="space-y-3">
            {insights?.map(ins => <InsightCard key={ins.id} insight={ins} />)}
            {(!insights || insights.length === 0) && (
              <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-8 text-center">
                <Brain className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Run audit to generate insights</p>
              </div>
            )}
          </div>
        </div>

        {/* Tasks */}
        <div className="col-span-5">
          <div className="flex items-center gap-2 mb-3">
            <ListTodo className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Fix Tasks</h2>
            <span className="text-[10px] text-slate-600">{tasks?.length || 0} tasks</span>
          </div>
          <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl overflow-hidden">
            {tasks?.map(t => <TaskRow key={t.id} task={t} />)}
            {(!tasks || tasks.length === 0) && (
              <div className="p-8 text-center">
                <ListTodo className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Run audit to generate tasks</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CATEGORY SCORES */}
      {audit?.category_scores && (
        <div>
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Category Scores</h2>
          <div className="grid grid-cols-6 gap-3">
            {Object.entries(audit.category_scores).map(([key, score]) => (
              <div key={key} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4 text-center">
                <div className={`text-2xl font-black tabular-nums ${score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400'}`} style={{ fontFamily: 'var(--font-heading)' }}>{score}</div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{key}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* INDEX HEALTH BREAKDOWN */}
      {index?.categories && (
        <div>
          <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3">Index Health Breakdown</h2>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(index.categories).map(([catKey, catData]) => (
              <div key={catKey} className={`rounded-xl border p-4 ${
                catKey === 'indekslenebilir' ? 'bg-emerald-500/5 border-emerald-500/20' :
                catKey === 'riskli_sayfalar' ? 'bg-red-500/5 border-red-500/20' :
                'bg-slate-800/30 border-slate-700/30'
              }`}>
                <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${
                  catKey === 'indekslenebilir' ? 'text-emerald-400' : catKey === 'riskli_sayfalar' ? 'text-red-400' : 'text-slate-400'
                }`}>{catKey.replace(/_/g, ' ')}</h3>
                <div className="space-y-1.5">
                  {Object.entries(catData).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">{k.replace(/_/g, ' ')}</span>
                      <span className={`font-bold tabular-nums ${v > 0 ? (catKey === 'riskli_sayfalar' ? 'text-red-400' : 'text-white') : 'text-slate-600'}`}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
